import { useState } from "react";
import { LinkSimple, PencilSimple, Trash, Check, X } from "@phosphor-icons/react";
import { UnitDTO } from "shared/types/unit";

export interface PublicatieCellProps {
  unit: UnitDTO;
  onImmowebChange: (unitId: string, referenceId: number | null) => Promise<void> | void;
  onYallyChange: (unitId: string, isPublished: boolean) => Promise<void> | void;
}

/**
 * Compact Publicatie cell used in leegstand-style tables. Stacks two small
 * status rows — Immoweb (with inline ref editor) and Yally Website — that
 * share the same pill shape so the column stays tight and visually aligned.
 */
export const PublicatieCell = ({
  unit,
  onImmowebChange,
  onYallyChange,
}: PublicatieCellProps) => {
  const [isEditingImmo, setIsEditingImmo] = useState(false);
  const [immoDraft, setImmoDraft] = useState("");
  const [isImmoSaving, setIsImmoSaving] = useState(false);
  const currentImmoId = unit.immowebReferenceId;

  const startEditImmo = () => {
    setImmoDraft(currentImmoId ? String(currentImmoId) : "");
    setIsEditingImmo(true);
  };

  const saveImmo = async () => {
    const trimmed = immoDraft.trim();
    const parsed = trimmed ? parseInt(trimmed, 10) : null;
    if (parsed !== null && (Number.isNaN(parsed) || parsed <= 0)) return;
    setIsImmoSaving(true);
    try {
      await onImmowebChange(unit.id as string, parsed);
      setIsEditingImmo(false);
    } finally {
      setIsImmoSaving(false);
    }
  };

  const clearImmo = async () => {
    setIsImmoSaving(true);
    try {
      await onImmowebChange(unit.id as string, null);
    } finally {
      setIsImmoSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1 min-w-[170px]">
      {/* Immoweb row */}
      <div className="group flex items-center gap-1 rounded-full border border-neutral-primary/15 bg-white px-2 py-0.5 text-xs">
        <span className="text-neutral-secondary-mid">Immoweb</span>
        {isEditingImmo ? (
          <div className="ml-auto flex items-center gap-1">
            <input
              type="text"
              value={immoDraft}
              onChange={(e) => setImmoDraft(e.target.value)}
              placeholder="Ref."
              disabled={isImmoSaving}
              className="w-20 rounded-sm border border-neutral-primary/20 px-1 py-0.5 text-xs focus:border-moss-primary focus:outline-hidden"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveImmo();
                }
                if (e.key === "Escape") setIsEditingImmo(false);
              }}
              autoFocus
            />
            <button
              onClick={saveImmo}
              disabled={isImmoSaving}
              className="text-moss-primary hover:text-moss-primary/80"
              title="Opslaan"
            >
              <Check size={12} weight="bold" />
            </button>
            <button
              onClick={() => setIsEditingImmo(false)}
              disabled={isImmoSaving}
              className="text-neutral-secondary-mid hover:text-neutral-primary"
              title="Annuleren"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ) : currentImmoId ? (
          <>
            <a
              href={`https://www.immoweb.be/nl/zoekertje/${currentImmoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto flex items-center gap-1 text-moss-primary hover:underline"
              title="Open op Immoweb"
            >
              <LinkSimple size={11} />
              #{currentImmoId}
            </a>
            <button
              onClick={startEditImmo}
              className="text-neutral-primary/50 hover:text-neutral-primary opacity-0 transition-opacity group-hover:opacity-100"
              title="Bewerken"
              disabled={isImmoSaving}
            >
              <PencilSimple size={11} />
            </button>
            <button
              onClick={clearImmo}
              className="text-red-500 hover:text-red-600 opacity-0 transition-opacity group-hover:opacity-100"
              title="Verwijderen"
              disabled={isImmoSaving}
            >
              <Trash size={11} />
            </button>
          </>
        ) : (
          <button
            onClick={startEditImmo}
            className="ml-auto text-moss-primary hover:text-moss-primary/80"
          >
            + Ref
          </button>
        )}
      </div>

      {/* Yally row — identical pill height so the two rows line up cleanly */}
      <button
        onClick={() => onYallyChange(unit.id as string, !unit.rentIsPublishedOnYallyWebsite)}
        className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors ${
          unit.rentIsPublishedOnYallyWebsite
            ? "border-moss-primary bg-moss-primary/10 text-moss-primary"
            : "border-neutral-primary/15 bg-white text-neutral-secondary-mid hover:bg-neutral-primary/5"
        }`}
        title={
          unit.rentIsPublishedOnYallyWebsite
            ? "Gepubliceerd op yally.apartments — klik om offline te halen"
            : "Niet gepubliceerd — klik om te publiceren"
        }
      >
        <span
          className={`flex h-3.5 w-3.5 items-center justify-center rounded-full ${
            unit.rentIsPublishedOnYallyWebsite
              ? "bg-moss-primary text-white"
              : "border border-neutral-primary/30 bg-white text-neutral-primary/40"
          }`}
          aria-hidden
        >
          {unit.rentIsPublishedOnYallyWebsite ? (
            <Check size={10} weight="bold" />
          ) : (
            <X size={10} weight="bold" />
          )}
        </span>
        <span>Yally website</span>
      </button>
    </div>
  );
};

export default PublicatieCell;
